import React, { useState } from 'react';
import { FileText, Link2, Loader2, Pencil, RotateCcw, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { C } from '@/design-system/dna';
import { errMsg, fmtDT } from './shared';
import { useHelp } from './Help';

const act = async (fn, ok, setData) => { try { const { data } = await fn(); setData?.(data); toast.success(ok); return data; } catch (e) { toast.error(errMsg(e, 'Falha.')); return null; } };

export function EditButtons({ editado, onEdit, onReset, testid }) {
  return (
    <span className="flex gap-1">
      <Button type="button" size="sm" variant="ghost" onClick={onEdit} data-testid={`${testid}-editar`}><Pencil className="h-3.5 w-3.5" /> Editar</Button>
      {editado && <Button type="button" size="sm" variant="ghost" onClick={onReset} data-testid={`${testid}-restaurar`}><RotateCcw className="h-3.5 w-3.5" /> Padrão</Button>}
    </span>
  );
}

export function CampoEditor({ k, h, onClose }) {
  const { setData } = useHelp();
  const [f, setF] = useState({ titulo: h.titulo, para_que: h.para_que, como: h.como, exemplo: h.exemplo || '', erros: h.erros || '' });
  const s = (key) => (e) => setF({ ...f, [key]: e.target.value });
  const save = async () => { if (await act(() => api.put(`/dna-mapa/ajuda/campo/${k}`, f), 'Texto de ajuda atualizado.', setData)) onClose(); };
  return (
    <div className="space-y-2 rounded-xl p-3" style={{ background: C.paper }} data-testid={`help-edit-campo-${k}`}>
      <Input value={f.titulo} onChange={s('titulo')} data-testid={`help-edit-campo-${k}-titulo`} />
      {[['para_que', 'Para que serve'], ['como', 'Como preencher'], ['exemplo', 'Exemplo'], ['erros', 'Erros comuns']].map(([key, l]) => (
        <label key={key} className="block text-[11px] font-semibold" style={{ color: C.muted }}>{l}<Textarea rows={2} value={f[key]} onChange={s(key)} data-testid={`help-edit-campo-${k}-${key}`} /></label>
      ))}
      <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={onClose}>Cancelar</Button><Button size="sm" onClick={save} data-testid={`help-edit-campo-${k}-salvar`}><Save className="h-4 w-4" /> Salvar</Button></div>
    </div>
  );
}

export function SecaoEditor({ s, onClose }) {
  const { setData } = useHelp();
  const [titulo, setTitulo] = useState(s.titulo);
  const [txt, setTxt] = useState(s.passos.join('\n'));
  const save = async () => { if (await act(() => api.put(`/dna-mapa/ajuda/secao/${s.id}`, { titulo, passos: txt.split('\n') }), 'Seção atualizada.', setData)) onClose(); };
  return (
    <div className="space-y-2 rounded-xl p-3" style={{ background: C.paper }} data-testid={`help-edit-secao-${s.id}`}>
      <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} data-testid={`help-edit-secao-${s.id}-titulo`} />
      <label className="block text-[11px] font-semibold" style={{ color: C.muted }}>Um passo por linha<Textarea rows={6} value={txt} onChange={(e) => setTxt(e.target.value)} data-testid={`help-edit-secao-${s.id}-passos`} /></label>
      <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={onClose}>Cancelar</Button><Button size="sm" onClick={save} data-testid={`help-edit-secao-${s.id}-salvar`}><Save className="h-4 w-4" /> Salvar</Button></div>
    </div>
  );
}

export const resetHelp = (kind, key, setData) => act(() => api.delete(`/dna-mapa/ajuda/${kind}/${key}`), 'Texto padrão restaurado.', setData);

export function MaterialUpload({ k, m }) {
  const { setData } = useHelp();
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [titulo, setTitulo] = useState(m.titulo);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const fd = new FormData(); fd.append('titulo', titulo); if (file) fd.append('file', file); else fd.append('url', url);
    setBusy(true); await act(() => api.post(`/dna-mapa/ajuda/material/${k}`, fd), 'Material publicado.', setData); setBusy(false); setFile(null); setUrl('');
  };
  return (
    <div className="mt-2 grid gap-2 rounded-xl p-3 sm:grid-cols-2" style={{ background: C.paper }} data-testid={`help-material-${k}-form`}>
      <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título" className="sm:col-span-2" data-testid={`help-material-${k}-titulo`} />
      <Input type="file" accept={k === 'manual' ? 'application/pdf' : 'video/mp4,video/webm'} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`help-material-${k}-arquivo`} />
      <div className="relative"><Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: C.muted }} /><Input value={url} onChange={(e) => setUrl(e.target.value)} disabled={!!file} placeholder="ou link https institucional" className="pl-9" data-testid={`help-material-${k}-link`} /></div>
      <div className="flex justify-end gap-2 sm:col-span-2">
        {m.disponivel && <Button size="sm" variant="ghost" onClick={() => resetHelp('material', k, setData)} data-testid={`help-material-${k}-remover`}>Remover</Button>}
        <Button size="sm" onClick={go} disabled={busy || (!file && !url.startsWith('https://'))} data-testid={`help-material-${k}-enviar`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Publicar</Button>
      </div>
    </div>
  );
}

export function MaterialView({ k, m }) {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!m.disponivel) return <p className="text-[12.5px]" style={{ color: C.muted }} data-testid={`help-material-${k}-indisponivel`}>Ainda não disponibilizado pela coordenação.</p>;
  if (m.url) return <a href={m.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-bold underline" style={{ color: C.purple }} data-testid={`help-material-${k}-abrir`}><Link2 className="h-4 w-4" /> Abrir {m.titulo}</a>;
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/dna-mapa/ajuda/material/${k}/arquivo`, { responseType: 'blob' }); setSrc(URL.createObjectURL(data)); }
    catch (e) { toast.error('Material indisponível no momento.'); } finally { setBusy(false); }
  };
  if (!src) return <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid={`help-material-${k}-abrir`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Abrir {m.titulo}</Button>;
  return k === 'video'
    ? <video src={src} controls controlsList="nodownload" className="w-full rounded-xl bg-black" data-testid={`help-material-${k}-player`} />
    : <iframe title={m.titulo} src={src} className="h-[60vh] w-full rounded-xl border" data-testid={`help-material-${k}-player`} />;
}

const ROLES = [['professor', 'Professores'], ['coordenacao', 'Coordenação'], ['direcao_pedagogica', 'Direção pedagógica']];

export function FeaturePanel({ rec, onChange }) {
  const [f, setF] = useState({ enabled: rec.enabled, roles: rec.roles });
  const has = (r) => f.roles.includes(r);
  const toggle = (r) => { const set = new Set(f.roles); const ids = r === 'professor' ? ['professor', 'teacher'] : [r]; ids.forEach((x) => (has(r) ? set.delete(x) : set.add(x))); setF({ ...f, roles: [...set] }); };
  const save = async () => { const d = await act(() => api.put('/dna-mapa/recurso', f), 'Disponibilização atualizada.'); if (d) onChange(d); };
  return (
    <div className="space-y-3 rounded-2xl border bg-white p-4" style={{ borderColor: C.line }} data-testid="dna-recurso-panel">
      <label className="flex items-center justify-between gap-3"><span><b className="text-[13px]" style={{ color: C.ink }}>Módulo habilitado</b><span className="block text-[11.5px]" style={{ color: C.muted }}>Desligar oculta o DNA Mapa e bloqueia as APIs para os perfis abaixo. Administradores mantêm acesso.</span></span><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="dna-recurso-enabled" /></label>
      <div className="flex flex-wrap gap-3">{ROLES.map(([r, l]) => <label key={r} className="flex items-center gap-1.5 text-[12.5px] font-semibold"><Checkbox checked={has(r)} onCheckedChange={() => toggle(r)} data-testid={`dna-recurso-role-${r}`} />{l}</label>)}</div>
      <div className="flex items-center justify-between gap-2"><span className="text-[11px]" style={{ color: C.muted }}>{rec.updated_at ? `Atualizado ${fmtDT(rec.updated_at)} por ${rec.updated_by?.name}` : 'Configuração padrão'}</span><Button size="sm" onClick={save} data-testid="dna-recurso-salvar"><Save className="h-4 w-4" /> Salvar</Button></div>
    </div>
  );
}
